import {$, Browser} from '@clappr/core'
import styles from './styles.scss'

const getScrollbarWidth = (): number => {
  const $element = $('<div/>')

  $element.css({
    position: 'absolute',
    top: -9999,
    width: 100,
    height: 100,
    overflow: 'scroll',
    msOverflowStyle: 'scrollbar'
  })

  $('body').append($element)

  const element = $element.get(0)
  const width = element.offsetWidth - element.clientWidth

  $element.remove()

  return width
}

const MIN_THUMB_SIZE = 20

/**
 * gemini-scrollbar
 * @version 1.5.3
 * @link http://noeldelgado.github.io/gemini-scrollbar/
 * @license MIT
 */
export class Scrollbar {
  private $el!: ZeptoCollection
  private $view!: ZeptoCollection
  private $scrollbar!: ZeptoCollection
  private $thumb!: ZeptoCollection
  private scrollbarWidth = 0
  private prevPageY = 0
  private cursorDown = false
  private naturalThumbSizeY!: number
  private scrollTopMax!: number
  private thumbSizeY!: number
  private trackTopMax!: number

  constructor($el: ZeptoCollection) {
    this.$el = $el
    this.scrollbarWidth = getScrollbarWidth()
    this.create()
  }

  private create(): void {
    this.$view = $('<div/>')
    this.$scrollbar = $('<div/>')
    this.$thumb = $('<div/>')

    const el = this.$el.get(0)
    const view = this.$view.get(0)

    while (el.childNodes.length > 0) {
      view.appendChild(el.childNodes[0])
    }

    this.$scrollbar.append(this.$thumb)
    this.$el.append(this.$scrollbar)
    this.$el.append(this.$view)

    this.$el.addClass(styles.scrollbarContainer)
    this.$view.addClass(styles.scrollbarView)
    this.$scrollbar.addClass(styles.scrollbar)
    this.$thumb.addClass(styles.scrollbarThumb)

    this.bindEvents()
  }

  public scrollToBottom(): void {
    this.$view.get(0).scrollTop = this.scrollTopMax
  }

  public update(): void {
    if (Browser.isMobile) {
      this.$view.css('width', this.$el.offset().width + this.scrollbarWidth)
    }

    const view = this.$view.get(0)
    const verticalScrollbar = this.$scrollbar.get(0)
    const verticalThumb = this.$thumb.get(0)

    this.naturalThumbSizeY =
      (verticalScrollbar.clientHeight / view.scrollHeight) *
      verticalScrollbar.clientHeight
    this.scrollTopMax = view.scrollHeight - view.clientHeight

    if (this.naturalThumbSizeY < MIN_THUMB_SIZE) {
      this.$thumb.css('height', MIN_THUMB_SIZE)
    } else if (this.scrollTopMax) {
      this.$thumb.css('height', this.naturalThumbSizeY)
    } else {
      this.$thumb.css('height', 0)
    }

    this.thumbSizeY = verticalThumb.clientHeight
    this.trackTopMax = verticalScrollbar.clientHeight - this.thumbSizeY
    this.scrollHandler()
  }

  public destroy(): void {
    this.unbinEvents()
    this.$scrollbar.remove()

    const el = this.$el.get(0)
    const view = this.$view.get(0)

    while (view.childNodes.length > 0) {
      el.appendChild(view.childNodes[0])
    }

    this.$view.remove()
    this.$el.removeClass(styles.scrollbarContainer)
  }

  private bindEvents(): void {
    this.$view.on('scroll', this.scrollHandler)
    this.$scrollbar.on('mousedown', this.clickTrackHandler)
    this.$thumb.on('mousedown', this.clickThumbHandler)
    $(document).on('mouseup', this.mouseUpDocumentHandler)
  }

  private unbinEvents(): void {
    this.$view.off('scroll', this.scrollHandler)
    this.$scrollbar.off('mousedown', this.clickTrackHandler)
    this.$thumb.off('mousedown', this.clickThumbHandler)
    $(document).off('mouseup', this.mouseUpDocumentHandler)
    $(document).off('mousemove', this.mouseMoveDocumentHandler)
  }

  private scrollHandler = (): void => {
    const y =
      (this.$view.scrollTop() * this.trackTopMax) / this.scrollTopMax || 0

    this.$thumb.css({
      msTransform: `translateY(${y}px)`,
      webkitTransform: `translate3d(0, ${y}px, 0)`,
      transform: `translate3d(0, ${y}px, 0)`
    })
  }

  private clickTrackHandler = (e: any): void => {
    if (e.target !== e.currentTarget) {
      return
    }

    const view = this.$view.get(0)
    const verticalScrollbar = this.$scrollbar.get(0)

    const offset = e.offsetY - this.naturalThumbSizeY / 2
    const thumbPositionPercentage =
      (offset * 100) / verticalScrollbar.clientHeight

    view.scrollTop = (thumbPositionPercentage * view.scrollHeight) / 100
  }

  private clickThumbHandler = (e: any): void => {
    this.startDrag()
    this.prevPageY = this.thumbSizeY - e.offsetY
  }

  private startDrag(): void {
    this.cursorDown = true

    $(document.body).addClass(styles.scrollbarDisableSelection)
    $(document).on('mousemove', this.mouseMoveDocumentHandler)
    document.onselectstart = () => false
  }

  private mouseUpDocumentHandler = (): void => {
    this.cursorDown = false
    $(document.body).removeClass(styles.scrollbarDisableSelection)
    $(document).off('mousemove', this.mouseMoveDocumentHandler)
    document.onselectstart = null
  }

  private mouseMoveDocumentHandler = (e: any): void => {
    if (this.cursorDown && this.prevPageY) {
      const view = this.$view.get(0)
      const verticalScrollbar = this.$scrollbar.get(0)

      const offset = e.clientY - verticalScrollbar.getBoundingClientRect().top
      const thumbClickPosition = this.thumbSizeY - this.prevPageY

      view.scrollTop =
        (this.scrollTopMax * (offset - thumbClickPosition)) / this.trackTopMax
    }
  }
}
